import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import DownloadIcon from "@mui/icons-material/Download";
import { useParams } from "react-router-dom";
import { getBrokerTransactions } from "../../../Services/BrokerTransactionsApi";
import { deleteBrokerTransaction } from "../../../Services/BrokerTransactionsApi";
import MakeBrokerTransactionDialog from "./MakeBrokerTransactionDialog";
import EditBrokerTransactionDialog from "./EditBrokerTransactionDialog";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

/*
  BrokerTransactionTap props:
  - brokerId (optional) : number|string
  If not provided, component uses useParams() to read brokerId.
*/

const BrokerTransactionTap = ({ brokerId: propBrokerId, initialUserId = null, initialBrokerData = null, initialUserPayload = null }) => {
  const params = useParams?.() || {};
  const brokerId = propBrokerId ?? params.brokerId ?? params.id ?? null;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all"); // all | withdraw | deposite
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // optional: simple pagination params
  const [pageParams, setPageParams] = useState({ page: 1, per_page: 100 });
  // فتح مودال إنشاء معاملة
  const [openMakeDialog, setOpenMakeDialog] = useState(false);
  const [makeDialogDefaults, setMakeDialogDefaults] = useState(null);
  const [refreshToggle, setRefreshToggle] = useState(false); // لتحديث البيانات بعد الإنشاء
  // تعديل معاملة
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  // payload مكوّن يحتوي message + data[{ broker_id }] لتمريره إلى مودال التعديل
  const [editingPayload, setEditingPayload] = useState(null);
  const [deletingId, setDeletingId] = useState(null); // id للمعاملة الجاري حذفها

  // احسب إجمالي السحب والإيداع من قائمة المعاملات الحالية
  const totals = useMemo(() => {
    let withdraw = 0;
    let deposit = 0;
    (transactions || []).forEach((t) => {
      const amt = parseFloat(t?.amount) || 0;
      const type = String(t?.type || "").toLowerCase();
      if (type.includes("withdraw")) withdraw += amt;
      else deposit += amt;
    });
    return { withdraw, deposit };
  }, [transactions]);

  useEffect(() => {
    // نحتاج brokerId أو initialUserId أو initialBrokerData أو initialUserPayload لإجراء الجلب
    if (!brokerId && !initialUserId && !initialBrokerData && !initialUserPayload) return;
    let mounted = true;
    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        // أولوية: إذا وُجد initialUserPayload و يحتوي user_id، نمرّر هذا المعرف فقط إلى الدالة
        const payloadUserId = initialUserPayload?.data?.[0]?.user_id ?? null;
        if (payloadUserId) {
          const res = await getBrokerTransactions(payloadUserId);
          const data = res?.data?.data ?? res?.data ?? [];
          if (mounted) setTransactions(Array.isArray(data) ? data : []);
          return;
        }

        // بعد ذلك: إذا وُجدت initialBrokerData (كائن الموزع) استخدم id الخاص بها
        if (initialBrokerData && initialBrokerData.id) {
          const res = await getBrokerTransactions(initialBrokerData.id);
          const data = res?.data?.data ?? res?.data ?? [];
          if (mounted) setTransactions(Array.isArray(data) ? data : []);
          return;
        }

        // ثم: إذا وُجد initialUserId استعمله
        if (initialUserId) {
          const res = await getBrokerTransactions(initialUserId);
          const data = res?.data?.data ?? res?.data ?? [];
          if (mounted) setTransactions(Array.isArray(data) ? data : []);
          return;
        }

        // خلاف ذلك: استعمل brokerId مع الفلاتر الاعتيادية
        const params = {
          ...pageParams,
          from: dateFrom || undefined,
          to: dateTo || undefined,
          type: typeFilter === "all" ? undefined : typeFilter,
          q: search || undefined,
        };
        Object.keys(params).forEach((k) => params[k] === undefined && delete params[k]);
        const res = await getBrokerTransactions(brokerId, params);
        const data = res?.data?.data ?? res?.data ?? [];
        if (mounted) setTransactions(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("getBrokerTransactions error:", err);
        if (mounted) setError(err?.response?.data?.message || err.message || "فشل جلب معاملات الموزع");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchData();
    return () => { mounted = false; };
  }, [brokerId, initialUserId, initialBrokerData, initialUserPayload, search, typeFilter, dateFrom, dateTo, pageParams, refreshToggle]);

  const fmtDate = (d) => {
    if (!d) return "-";
    try {
      return new Date(d).toLocaleString("ar-EG");
    } catch { return d; }
  };

  // ترجمة نوع المعاملة إلى العربية
  const mapTypeToArabic = (type) => {
    if (!type) return "-";
    const t = String(type).toLowerCase();
    if (t === "withdraw" || t === "سحب") return "سحب";
    if (t === "deposite" || t === "deposit" || t === "إيداع" || t === "ايداع") return "إيداع";
    // fallback — إذا كان هناك اسم آخر نعرضه كما هو
    return type;
  };

  const escapeCsv = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const buildCsv = (rows) => {
    const headers = ["معرف", "المبلغ", "النوع", "تاريخ العملية", "ملاحظة", "مستخدم (id)", "اسم المستخدم", "بريد المستخدم", "تاريخ الإنشاء"];
    const lines = [headers.map(escapeCsv).join(",")];
    rows.forEach((r) => {
      const line = [
        r.id ?? "",
        r.amount ?? "",
        mapTypeToArabic(r.type) ?? "",
        r.date ?? "",
        r.note ?? "",
        r.user?.id ?? r.user_id ?? "",
        r.user?.name ?? "",
        r.user?.email ?? "",
        r.created_at ?? "",
      ];
      lines.push(line.map(escapeCsv).join(","));
    });
    return lines.join("\n");
  };

  const handleExport = () => {
    try {
      const csv = buildCsv(transactions || []);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const now = new Date();
      const fname = `broker_${brokerId}_transactions_${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}.csv`;
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export error:", err);
    }
  };

  return (
    <Box dir="rtl" sx={{ p: 3 }}>

      {/* بطاقة ملخص الموزع — تعرض القيم المالية إن توفرت initialBrokerData */}
      {initialBrokerData && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {initialBrokerData.name ?? initialBrokerData.user?.name ?? "موزع"}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {initialBrokerData.phone1 ?? initialBrokerData.user?.phone ?? "-"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>نسبة العمولة</Typography>
                <Typography sx={{ fontWeight: 700 }}>{initialBrokerData.commetion ?? "-"}</Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>المتبقي</Typography>
                <Typography sx={{ fontWeight: 700 }}>{initialBrokerData.remmaning ?? "-"}</Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>المدفوع</Typography>
                <Typography sx={{ fontWeight: 700 }}>{initialBrokerData.paied ?? "-"}</Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>الرصيد</Typography>
                <Typography sx={{ fontWeight: 700 }}>{initialBrokerData.balance ?? "-"}</Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>التأمين</Typography>
                <Typography sx={{ fontWeight: 700 }}>{initialBrokerData.insurance ?? "-"}</Typography>
              </Box>
            </Box>
          </Box>

          {/* صف الإجماليات: إجمالي السحب وإجمالي الإيداع */}
          <Box sx={{ mt: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-start" }}>
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>إجمالي السحب</Typography>
              <Typography sx={{ fontWeight: 700, color: "error.main" }}>{totals.withdraw.toFixed(2)} ج</Typography>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>إجمالي الإيداع</Typography>
              <Typography sx={{ fontWeight: 700, color: "success.main" }}>{totals.deposit.toFixed(2)} ج</Typography>
            </Box>
          </Box>
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="ابحث بمعرف المعاملة، الملاحظة أو اسم المستخدم..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260, flex: "1 1 300px" }}
            InputProps={{
              startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}><ClearIcon fontSize="small" /></IconButton>
                </InputAdornment>
              ) : null
            }}
          />

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="type-filter-label">النوع</InputLabel>
            <Select labelId="type-filter-label" value={typeFilter} label="النوع" onChange={(e) => setTypeFilter(e.target.value)}>
              <MenuItem value="all">الكل</MenuItem>
              <MenuItem value="withdraw">سحب</MenuItem>
              <MenuItem value="deposite">ايداع</MenuItem>
            </Select>
          </FormControl>

          <TextField label="من" type="date" size="small" value={dateFrom} onChange={(e)=>setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="إلى" type="date" size="small" value={dateTo} onChange={(e)=>setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />

          {/* أزرار السحب والإيداع */}
          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              // user_id نمرره للحوار (أولوية للمصدر المتاح)
              const userId = initialBrokerData?.user?.id ?? initialBrokerData?.user_id ?? initialUserId ?? brokerId;
              setMakeDialogDefaults({
                brokerId: userId,
                type: "withdraw",
                date: new Date().toISOString().split("T")[0],
                amount: "",
                note: "",
              });
              setOpenMakeDialog(true);
            }}
            sx={{ ml: "auto" }}
          >
            سحب
          </Button>

          <Button
            variant="contained"
            color="success"
            onClick={() => {
              const userId = initialBrokerData?.user?.id ?? initialBrokerData?.user_id ?? initialUserId ?? brokerId;
              setMakeDialogDefaults({
                brokerId: userId,
                type: "deposite",
                date: new Date().toISOString().split("T")[0],
                amount: "",
                note: "",
              });
              setOpenMakeDialog(true);
            }}
            sx={{ ml: 1 }}
          >
            إيداع
          </Button>

          <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleExport} sx={{ ml: 1 }}>
            تصدير CSV
          </Button>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Paper sx={{ p: 2, background: "#ffecec" }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      ) : (
        <Paper sx={{ p: 1 }}>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="right">المبلغ</TableCell>
                  <TableCell align="right">النوع</TableCell>
                  <TableCell align="right">تاريخ العملية</TableCell>
                  <TableCell align="right">ملاحظة</TableCell>
                  <TableCell align="right">مستخدم (id)</TableCell>
                  <TableCell align="right">اسم المستخدم</TableCell>
                  <TableCell align="right">بريد المستخدم</TableCell>
                  <TableCell align="right">تاريخ الإنشاء</TableCell>
                  <TableCell align="right">إجراءات</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">لا توجد معاملات</TableCell>
                  </TableRow>
                ) : (
                  transactions.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell align="right">{t.amount ? `${t.amount} ج` : "-"}</TableCell>
                      <TableCell align="right">{/* النوع بالعربي */ mapTypeToArabic(t.type) ?? "-"}</TableCell>
                      <TableCell align="right">{t.date ?? "-"}</TableCell>
                      <TableCell align="right" sx={{ maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={t.note ?? ""}>
                        {t.note ?? "-"}
                      </TableCell>
                      <TableCell align="right">{t.user?.id ?? t.user_id ?? "-"}</TableCell>
                      <TableCell align="right">{t.user?.name ?? "-"}</TableCell>
                      <TableCell align="right">{t.user?.email ?? "-"}</TableCell>
                      <TableCell align="right">{fmtDate(t.created_at)}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingTransaction(t);
                            // جهّز payload يحتوي broker_id (أولوية للحقل الموجود في السجل)
                            const bid = t?.broker_id ?? t?.transaction_broker?.id ?? t?.user?.id ?? t?.user_id ?? null;
                            setEditingPayload({
                              message: "✅ تم جلب بيانات العمليات بنجاح",
                              data: [{ broker_id: bid }],
                            });
                            setOpenEditDialog(true);
                          }}
                          title="تعديل المعاملة"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>

                        <IconButton
                          size="small"
                          color="error"
                          onClick={async (e) => {
                            e.stopPropagation();
                            const ok = window.confirm(`هل أنت متأكد من حذف المعاملة #${t.id}؟`);
                            if (!ok) return;
                            try {
                              setDeletingId(t.id);
                              const token = localStorage.getItem("authToken") || null;
                              await deleteBrokerTransaction(t.id, token);
                              setRefreshToggle((v) => !v);
                            } catch (err) {
                              console.error("deleteBrokerTransaction error:", err);
                              alert(err?.response?.data?.message || err?.message || "فشل حذف المعاملة");
                            } finally {
                              setDeletingId(null);
                            }
                          }}
                          title="حذف المعاملة"
                          sx={{ ml: 1 }}
                        >
                          {deletingId === t.id ? <CircularProgress size={18} /> : <DeleteIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Make Transaction Dialog */}
      <MakeBrokerTransactionDialog
        open={openMakeDialog}
        onClose={() => setOpenMakeDialog(false)}
        brokerId={makeDialogDefaults?.brokerId}
        defaults={{
          type: makeDialogDefaults?.type,
          date: makeDialogDefaults?.date,
          amount: makeDialogDefaults?.amount,
          note: makeDialogDefaults?.note,
        }}
        onSuccess={() => {
          // أغلق وحفز إعادة الجلب
          setOpenMakeDialog(false);
          setMakeDialogDefaults(null);
          setRefreshToggle((v) => !v);
        }}
      />

      {/* Edit Transaction Dialog */}
      <EditBrokerTransactionDialog
        open={openEditDialog}
        onClose={() => { setOpenEditDialog(false); setEditingTransaction(null); setEditingPayload(null); }}
        transaction={editingTransaction}
        initialUserPayload={editingPayload} // نمرّر payload يحتوي broker_id
        onSuccess={() => {
          setOpenEditDialog(false);
          setEditingTransaction(null);
          setEditingPayload(null);
          setRefreshToggle((v) => !v);
        }}
      />
    </Box>
  );
};

export default BrokerTransactionTap;
